/**
 * THE GUIDE FILE FORMAT (31 Oct, HLP-45; One and Help v2 S3).
 *
 * A guide is a markdown file in the platform repo, `guides/<key>.md`. It is
 * human-written; a migration loads it into `guide` / `guide_section` and
 * their locale variants (rule 6), and the file wins if the two disagree - the
 * same posture as the help corpus since 155. A change to a guide is a change
 * to the file AND a migration generated from it
 * (`supabase/scripts/guides_to_sql.mts`); the web suite fails when the file's
 * md5 is not the one the newest guide migration loaded.
 *
 * This module is DEPENDENCY-FREE on purpose: the generator runs it under
 * `node --experimental-strip-types`, and the renderer and the suite import it
 * as ordinary TypeScript. One parser, so the loaded text and the rendered text
 * cannot be read two ways (rule 11).
 *
 * ── THE FILE ────────────────────────────────────────────────────────────────
 *
 *   ---
 *   key: store-manager          the route segment, never renamed
 *   audience: store_manager     who it is FOR and who may READ it (353, D5
 *                               revised): 'everyone' = any member; a role
 *                               audience = people who can open the pages it
 *                               teaches, plus support-desk holders and tenant
 *                               administrators. app.may_read_guide is the one
 *                               judgement. 349's restricted_to is RETIRED and
 *                               the parser refuses it.
 *   sort: 20
 *   title: Running a stock room
 *   lede: One sentence under the title.
 *   ---
 *   ## Section title {#section-key}
 *   ...body...
 *   ## Section title {#section-key page=/help?new=ticket}
 *                             optional (SUP-2): the page where the section's
 *                             task is done; English files only, not text
 *
 * ── THE BODY (a small, closed subset - anything else is a paragraph) ────────
 *
 *   paragraph lines           joined into one paragraph
 *   - item                    a list
 *   1. item                   numbered steps
 *   > [!note|rule|warn] Label a callout; following `> ` lines are its text
 *   Term :: Definition        a definition list (consecutive lines)
 *   | a | b |  + |---|        a table, first row the header
 *   ?? Question               a folded question; following lines answer it
 *   :::columns ... :::        side-by-side lists, each started by `#### Title`
 *   ::widget-name             an interactive part the app draws (GUIDE_WIDGETS)
 *
 * Inline: **bold**, `code`, [[Words on a button]], ((Pill)), [text](#/route)
 * (a `#/` route only), and {{KEY}} / {{KEY|arg}} - THE APP'S OWN COPY, filled
 * from `lib/guideCopy.ts`, so rewording a button rewords its guide. The suite
 * fails on a {{KEY}} the registry does not hold.
 */

export type GuideMeta = {
  key: string
  audience: string
  sort: number
  title: string
  lede: string
  /** A translation only: the fingerprint of the English title + lede it was
   *  translated FROM (see "A SPANISH TWIN" below). */
  from?: string
}

export type GuideFileSection = {
  key: string
  title: string
  body: string
  /** A translation only: the fingerprint of the English section it was
   *  translated FROM, written in the file - never computed from today's English. */
  from?: string
  /** SUP-2 (1 Nov). The page where this section's task is DONE -
   *  `## Open a ticket {#ticket page=/help?new=ticket}`. English files only
   *  (a page is not translated); the generator checks it against
   *  supabase/functions/ask-one/pages.ts `isActionPage`. NOT in the
   *  fingerprint - nobody reads it - so adding one needs no migration: it
   *  reaches One through ask-one/guidePages.generated.ts. */
  page?: string
}

export type GuideFile = { meta: GuideMeta; sections: GuideFileSection[] }

/*
 * ── A SPANISH TWIN (S5, HLP-47) ─────────────────────────────────────────────
 *
 * `guides/<key>.es-CL.md` is an everyone guide's translation: the same front
 * matter plus `from: <md5>`, and the SAME section keys, each written
 * `## Título {#key from=<md5>}`. Each `from` is 113's source_fingerprint -
 * `app.variant_fingerprint(english title, english body)` (or title + lede for
 * the guide) - of the ENGLISH THE TRANSLATOR READ. It is a literal in the file
 * and the generator copies it; it must NEVER be computed from the English as it
 * stands when the migration is generated, or regenerating after an English
 * edit would silently bless the stale Spanish. After an English edit the
 * section's `from` no longer matches and `guides()` falls back to English for
 * that section, row by row, until somebody re-translates it and stamps the new
 * fingerprint (`guides_to_sql.mts --fingerprints <key>` prints them).
 */

/** The locale a guide FILE is in, from its name: `<key>.md` is en-CA,
 *  `<key>.<locale>.md` a translation. */
export function guideFileLocale(fileName: string): { key: string; locale: string } | null {
  const m = fileName.match(/^([a-z0-9-]+)(?:\.([a-z]{2}-[A-Z]{2}))?\.md$/)
  return m ? { key: m[1], locale: m[2] ?? 'en-CA' } : null
}

/** 113's fingerprint input for one English pair (title + body, or title +
 *  lede): md5 of this is app.variant_fingerprint. The caller hashes; this
 *  module stays dependency-free. */
export function variantFingerprintSource(title: string, body: string): string {
  return title + String.fromCharCode(31) + body
}

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'b'; v: Inline[] }
  | { t: 'code'; v: string }
  | { t: 'ui'; v: Inline[] }
  | { t: 'pill'; v: string }
  | { t: 'link'; v: Inline[]; href: string }
  | { t: 'copy'; key: string; arg: string | null }

export type Block =
  | { t: 'p'; v: Inline[] }
  | { t: 'ul'; items: Inline[][] }
  | { t: 'steps'; items: Inline[][] }
  | { t: 'note'; tone: 'note' | 'rule' | 'warn'; label: Inline[]; v: Inline[] }
  | { t: 'dl'; items: { term: Inline[]; def: Inline[] }[] }
  | { t: 'table'; head: Inline[][]; rows: Inline[][][] }
  | { t: 'q'; q: Inline[]; a: Inline[] }
  | { t: 'columns'; cols: { title: Inline[]; items: Inline[][] }[] }
  | { t: 'widget'; name: string }

/** Line endings normalised, so a CRLF checkout loads the same bytes as CI. */
export function normaliseGuideText(text: string): string {
  return text.replace(/\r\n?/g, '\n')
}

const SECTION = /^## (.+?) \{#([a-z0-9-]+)(?: page=(\/[A-Za-z0-9/_?=&-]{0,80}))?(?: from=([0-9a-f]{32}))?\}\s*$/

/** A whole file into its metadata and sections. Throws on a malformed file:
 *  the generator must refuse rather than load half a guide. */
export function splitGuideFile(raw: string): GuideFile {
  const text = normaliseGuideText(raw)
  const m = text.match(/^---\n([\s\S]*?)\n---\n/)
  if (!m) throw new Error('guide: no front matter')
  const fm: Record<string, string> = {}
  for (const line of m[1].split('\n')) {
    const kv = line.match(/^([a-z_]+):\s*(.*)$/)
    if (kv) fm[kv[1]] = kv[2].trim()
  }
  for (const k of ['key', 'audience', 'sort', 'title', 'lede']) {
    if (!fm[k]) throw new Error(`guide: front matter has no ${k}`)
  }
  if (!/^[a-z0-9-]+$/.test(fm.key)) throw new Error(`guide: bad key ${fm.key}`)
  const meta: GuideMeta = {
    key: fm.key,
    audience: fm.audience,
    sort: Number(fm.sort),
    title: fm.title,
    lede: fm.lede,
    ...(fm.from ? { from: fm.from } : {}),
  }
  // 353: reading follows audience; a second "who may read" line would be a
  // second answer (rule 11), so a file that still carries one is refused.
  if (fm.restricted_to !== undefined) throw new Error('guide: restricted_to is retired (353) - reading follows audience')
  if (meta.from !== undefined && !/^[0-9a-f]{32}$/.test(meta.from)) throw new Error('guide: from is not an md5')
  const sections: GuideFileSection[] = []
  let cur: GuideFileSection | null = null
  const lines: string[] = []
  const flush = () => {
    if (cur) {
      cur.body = lines.join('\n').trim()
      sections.push(cur)
    }
    lines.length = 0
  }
  for (const line of text.slice(m[0].length).split('\n')) {
    const s = line.match(SECTION)
    if (s) {
      flush()
      cur = { key: s[2], title: s[1].trim(), body: '', ...(s[3] ? { page: s[3] } : {}), ...(s[4] ? { from: s[4] } : {}) }
    } else if (cur) {
      lines.push(line)
    } else if (line.trim()) {
      throw new Error('guide: text before the first section')
    }
  }
  flush()
  if (sections.length === 0) throw new Error('guide: no sections')
  const keys = new Set(sections.map((x) => x.key))
  if (keys.size !== sections.length) throw new Error('guide: a section key is used twice')
  return { meta, sections }
}

/** SUP-2. Every section's action page, keyed "<guide>/<section>", from the
 *  ENGLISH files - what guides_to_sql.mts writes to ask-one's
 *  guidePages.generated.ts and the suite compares it with. Sorted, so the
 *  generated file does not churn. */
export function guidePagesOf(files: readonly GuideFile[]): Record<string, string> {
  const pairs: Array<[string, string]> = []
  for (const f of files) for (const s of f.sections) if (s.page) pairs.push([`${f.meta.key}/${s.key}`, s.page])
  pairs.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
  return Object.fromEntries(pairs)
}

/**
 * THE FINGERPRINT the migration checks and the suite recomputes: the guide's
 * title and lede, then each section's key, title and body in order, separated
 * by the unit/record separators (chr 31 / chr 30). The SQL side is
 * `app.guide_fingerprint(guide_id, locale)` - change both together.
 */
export function guideFingerprintSource(f: GuideFile): string {
  const US = String.fromCharCode(31)
  const RS = String.fromCharCode(30)
  return [f.meta.title + US + f.meta.lede, ...f.sections.map((s) => s.key + US + s.title + US + s.body)].join(RS)
}

/* ── inline ─────────────────────────────────────────────────────────────── */

const INLINE = /\*\*(.+?)\*\*|`([^`]+)`|\[\[(.+?)\]\]|\(\((.+?)\)\)|\[([^\]]+)\]\((#\/[A-Za-z0-9/_-]*)\)|\{\{([A-Za-z_][A-Za-z0-9_.]*)(?:\|([^}]*))?\}\}/g

export function parseInline(text: string): Inline[] {
  const out: Inline[] = []
  let at = 0
  for (const m of text.matchAll(INLINE)) {
    const i = m.index ?? 0
    if (i > at) out.push({ t: 'text', v: text.slice(at, i) })
    if (m[1] !== undefined) out.push({ t: 'b', v: parseInline(m[1]) })
    else if (m[2] !== undefined) out.push({ t: 'code', v: m[2] })
    else if (m[3] !== undefined) out.push({ t: 'ui', v: parseInline(m[3]) })
    else if (m[4] !== undefined) out.push({ t: 'pill', v: m[4] })
    else if (m[5] !== undefined) out.push({ t: 'link', v: parseInline(m[5]), href: m[6] })
    else out.push({ t: 'copy', key: m[7], arg: m[8] ?? null })
    at = i + m[0].length
  }
  if (at < text.length) out.push({ t: 'text', v: text.slice(at) })
  return out
}

/** Every {{KEY}} a text uses - what the suite checks against the registry. */
export function copyKeysIn(text: string): string[] {
  const keys: string[] = []
  const walk = (xs: Inline[]) => {
    for (const x of xs) {
      if (x.t === 'copy') keys.push(x.key)
      else if (x.t === 'b' || x.t === 'ui' || x.t === 'link') walk(x.v)
    }
  }
  for (const line of normaliseGuideText(text).split('\n')) walk(parseInline(line))
  return keys
}

/* ── blocks ─────────────────────────────────────────────────────────────── */

const isTable = (l: string) => /^\|.*\|\s*$/.test(l)
const cells = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim())

export function parseBlocks(body: string): Block[] {
  const lines = normaliseGuideText(body).split('\n')
  const out: Block[] = []
  let i = 0
  const para: string[] = []
  const flushPara = () => {
    if (para.length) out.push({ t: 'p', v: parseInline(para.join(' ')) })
    para.length = 0
  }
  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()
    if (!trimmed) {
      flushPara()
      i++
      continue
    }
    const widget = trimmed.match(/^::([a-z][a-z0-9-]*)$/)
    if (widget) {
      flushPara()
      out.push({ t: 'widget', name: widget[1] })
      i++
      continue
    }
    if (trimmed === ':::columns') {
      flushPara()
      const cols: { title: Inline[]; items: Inline[][] }[] = []
      i++
      while (i < lines.length && lines[i].trim() !== ':::') {
        const l = lines[i].trim()
        const h = l.match(/^#### (.+)$/)
        if (h) cols.push({ title: parseInline(h[1]), items: [] })
        else if (l.startsWith('- ') && cols.length) cols[cols.length - 1].items.push(parseInline(l.slice(2)))
        i++
      }
      i++
      out.push({ t: 'columns', cols })
      continue
    }
    const note = trimmed.match(/^> \[!(note|rule|warn)\]\s*(.*)$/)
    if (note) {
      flushPara()
      const text: string[] = []
      i++
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        text.push(lines[i].trim().replace(/^>\s?/, ''))
        i++
      }
      out.push({ t: 'note', tone: note[1] as 'note' | 'rule' | 'warn', label: parseInline(note[2]), v: parseInline(text.join(' ')) })
      continue
    }
    if (trimmed.startsWith('?? ')) {
      flushPara()
      const q = parseInline(trimmed.slice(3))
      const a: string[] = []
      i++
      while (i < lines.length && lines[i].trim() && !lines[i].trim().startsWith('?? ')) {
        a.push(lines[i].trim())
        i++
      }
      out.push({ t: 'q', q, a: parseInline(a.join(' ')) })
      continue
    }
    if (isTable(trimmed)) {
      flushPara()
      const rows: string[] = []
      while (i < lines.length && isTable(lines[i].trim())) {
        rows.push(lines[i].trim())
        i++
      }
      const body = rows.filter((r) => !/^\|[\s:|-]+\|$/.test(r))
      out.push({
        t: 'table',
        head: cells(body[0]).map(parseInline),
        rows: body.slice(1).map((r) => cells(r).map(parseInline)),
      })
      continue
    }
    if (/^- /.test(trimmed) || /^\d+\. /.test(trimmed)) {
      flushPara()
      const steps = /^\d+\. /.test(trimmed)
      const items: Inline[][] = []
      while (i < lines.length && (steps ? /^\d+\. /.test(lines[i].trim()) : /^- /.test(lines[i].trim()))) {
        items.push(parseInline(lines[i].trim().replace(steps ? /^\d+\. / : /^- /, '')))
        i++
      }
      out.push(steps ? { t: 'steps', items } : { t: 'ul', items })
      continue
    }
    if (/ :: /.test(trimmed) && !para.length) {
      const items: { term: Inline[]; def: Inline[] }[] = []
      while (i < lines.length && / :: /.test(lines[i])) {
        const [term, ...rest] = lines[i].trim().split(' :: ')
        items.push({ term: parseInline(term), def: parseInline(rest.join(' :: ')) })
        i++
      }
      out.push({ t: 'dl', items })
      continue
    }
    para.push(trimmed)
    i++
  }
  flushPara()
  return out
}

/** Every `::widget` a body names - the suite checks each one is drawn. */
export function widgetsIn(body: string): string[] {
  return parseBlocks(body).flatMap((b) => (b.t === 'widget' ? [b.name] : []))
}

/** Plain words of a body with every {{KEY}} filled - for search and for the
 *  phone's "On this page" picker. `fill` is the registry's resolver. */
export function inlineText(xs: Inline[], fill: (key: string, arg: string | null) => string): string {
  return xs
    .map((x) =>
      x.t === 'text' || x.t === 'code' || x.t === 'pill'
        ? x.v
        : x.t === 'copy'
          ? fill(x.key, x.arg)
          : inlineText(x.v, fill),
    )
    .join('')
}
